import * as types from 'constants/ActionTypes';
import { USERS_API_URL, USERS_FILTER_URL, USERS_USERNAME_CHECK_URL } from 'constants/ApiConstants';
import { CURRENT_USER_DATA_FAILURE, CURRENT_USER_DATA_REQUEST, CURRENT_USER_DATA_SUCCESS } from 'modules/users/actionTypes';

/**
 * @typedef {{
 *  pageLink: string,
 *  params: {
 *   dealerId?: string,
 *   groupId?: string,
 *   fields: Array<string>,
 *   page: number,
 *   paginationType?: 'SLICE' | 'PAGE',
 *   roles?: Array<import('modules/users/types/users.enum').UserRoleEnum>,
 *   status?: Array<import('modules/users/types/users.enum').UserStatusEnum>,
 *   text?: string,
 *  },
 * }} FetchUsersPayloadAction
 * @typedef {import("modules/global/generic.types.js").Action<FetchUsersPayloadAction>} FetchUsersAction
 */
export const fetchUsers = (params, meta = {}) => ({
  type: types.FETCH_USERS_REQUEST,
  payload: {
    request: {
      url: USERS_FILTER_URL,
      method: 'GET',
      params: {
        ...params,
        fields: 'id,displayName,username,roles,status,firstName,lastName',
      },
    },
  },
  meta,
});
/**
 * @typedef {{
 *  error: string,
 * }} FetchUsersFailurePayloadAction
 * @typedef {import("modules/global/generic.types.js").Action<FetchUsersFailurePayloadAction>} FetchUsersFailureAction
 */
export const fetchUsersFailure = error => ({
  type: types.FETCH_USERS_FAILURE,
  payload: error,
  error: true,
});

/**
 * @typedef {{
 *  users: Array<import('modules/users/types/projections/user').User>,
 * }} FetchUsersSuccessPayloadAction
 * @typedef {import("modules/global/generic.types.js").Action<FetchUsersSuccessPayloadAction>} FetchUsersSuccessAction
 */
export const fetchUsersSuccess = (users, meta = {}) => ({
  type: types.FETCH_USERS_SUCCESS,
  payload: users,
  meta,
});

// SINGLE USER
export const fetchUser = (userId, meta = {}) => ({
  type: types.FETCH_USER_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${USERS_API_URL}/${userId}`,
    },
    userId,
  },
  meta,
});

export const fetchUserFailure = (error, meta) => ({
  type: types.FETCH_USER_FAILURE,
  error,
  meta,
});

export const fetchUserSuccess = (user, meta = {}) => ({
  type: types.FETCH_USER_SUCCESS,
  payload: user,
  meta,
});

export const initDashboard = () => ({
  type: types.INIT_USERS_DASHBOARD,
});

// UPDATE USER
export const updateUser = ({ user, dryRun = false }, meta) => ({
  type: types.UPDATE_USER_REQUEST,
  payload: {
    request: {
      method: 'PUT',
      url: `${USERS_API_URL}/${user.id}`,
      params: { dryRun },
      data: user,
    },
    id: user.id,
  },
  meta,
});

export const updateUserFailure = error => ({
  type: types.UPDATE_USER_FAILURE,
  error,
});

export const updateUserSuccess = (payload, meta) => ({
  type: types.UPDATE_USER_SUCCESS,
  payload,
  meta,
});

// CREATE USER
export const createUser = ({ payload, meta }) => ({
  type: types.CREATE_USER_REQUEST,
  payload,
  meta,
});

export const createUserFailure = ({ error }) => ({
  type: types.CREATE_USER_FAILURE,
  error,
});

export const createUserSuccess = ({ payload }) => ({
  type: types.CREATE_USER_SUCCESS,
  payload,
});

/**
 * Add user filters
 * @param {{[key:string] : string}} filters
 */
export const setUserFilter = filters => ({
  type: types.SET_USERS_FILTER,
  payload: filters,
});

export const removeFilter = (field, value, isArray = false) => ({
  type: types.REMOVE_USER_FILTER,
  payload: {
    field,
    value,
  },
  meta: {
    isArray,
  },
});

export const resetFilters = () => ({
  type: types.RESET_USER_FILTER,
});

// AUTHENTICATION LOGOUT

export const logoutUserClear = () => ({
  type: types.USER_LOGOUT_CLEAR,
});

// USER EMAIL UNICITY CHECK
export const userExistsRequest = username => ({
  type: types.USER_EXISTS_REQUEST,
  payload: {
    request: {
      method: 'HEAD',
      url: USERS_USERNAME_CHECK_URL,
      params: { username },
    },
  },
});

export const userExistsFailure = message => ({
  type: types.USER_EXISTS_FAILURE,
  payload: message,
  error: true,
});

export const userExistsSuccess = payload => ({
  type: types.USER_EXISTS_SUCCESS,
  payload,
});

export const cleanUserExistRequest = () => ({
  type: types.CLEAN_USER_EXISTS_REQUEST,
});

// USERS

/**
 * @param {string} id
 * @returns {import('modules/global/generic.types').Action<string>}
 */
export const fetchDataForCurrentUser = (id, meta = {}) => ({
  type: CURRENT_USER_DATA_REQUEST,
  payload: id,
  meta,
});

/**
 * @param {import('modules/users/types/actions').APIUser} userData
 * @returns {import('modules/users/types/actions').FetchDataForCurrentUserSuccessAction}
 */
export const fetchDataForCurrentUserSuccess = (userData, meta = {}) => ({
  type: CURRENT_USER_DATA_SUCCESS,
  payload: userData,
  meta,
});

export const fetchDataForCurrentUserFailure = (error, meta = {}) => ({
  type: CURRENT_USER_DATA_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const fetchAssignedUsers = params => ({
  type: types.FETCH_ASSIGNED_USERS,
  payload: {
    request: {
      method: 'GET',
      url: USERS_FILTER_URL,
      params,
    },
  },
});

export const fetchAssignedUsersSuccess = payload => ({
  type: types.FETCH_ASSIGNED_USERS_SUCCESS,
  payload,
});

export const fetchAssignedUsersFailure = error => ({
  type: types.FETCH_ASSIGNED_USERS_FAILURE,
  payload: {
    message: error,
  },
  error: true,
});

export const cleanAssignedUsers = () => ({
  type: types.CLEAN_ASSIGNED_USERS,
});

export const fetchAssignedUsersNextPage = () => ({
  type: types.FETCH_ASSIGNED_USERS_NEXT_PAGE,
});

/**
 * @typedef {import('modules/global/generic.types').Action<undefined>} IsDetailsPageReadyAction
 * @param {undefined} payload
 * @param {Object} [meta={}]
 * @returns {IsDetailsPageReadyAction}
 */
export const isDetailsPageReady = (payload, meta = {}) => ({
  type: types.USER_DETAILS_PAGE_IS_READY,
  payload,
  meta,
});
